import React from 'react';
import { useRoute, Link } from 'wouter';
import { ArrowLeft, ChevronRight, ArrowRight, HelpCircle } from 'lucide-react';
import { tools } from '../../utils/tools';
import { generateCategorySlug } from '../../utils/slugs';
import { SEO } from '../SEO';
import { Navigation } from '../Layout/Navigation';
import { ToolCard } from '../directory/ToolCard';

export function CategoryPage() {
  const [match, params] = useRoute('/categories/:categorySlug');

  if (!match) return null;

  const categorySlug = params?.categorySlug;

  // Find all tools in this category
  const categoryTools = tools.filter(t =>
    generateCategorySlug(t.category) === categorySlug
  );

  if (categoryTools.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navigation />
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Category Not Found</h1>
          <p className="text-gray-600 mb-8">The category you're looking for doesn't exist.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/directory">
              <a className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500">
                <ArrowLeft className="w-4 h-4" />
                Browse All Categories
              </a>
            </Link>
            <Link href="/questionnaire">
              <a className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Get Personalized Picks
                <ArrowRight className="w-4 h-4" />
              </a>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryName = categoryTools[0].category;
  const toolNames = categoryTools.slice(0, 5).map(t => t.name).join(', ');
  const topFreeTool = categoryTools.find(t => t.pricing.includes('free'))?.name;
  const topToolName = categoryTools[0].name;

  const faqItems = [
    {
      question: `What is the best ${categoryName.toLowerCase()} software for small businesses?`,
      answer: `The best ${categoryName.toLowerCase()} software for small businesses depends on your budget, team size, and goals. Top options include ${categoryTools.slice(0, 3).map(t => t.name).join(', ')}. Use btoolme's free questionnaire to get a personalized recommendation for your specific situation.`
    },
    {
      question: `Is there a free ${categoryName.toLowerCase()} tool for startups?`,
      answer: topFreeTool
        ? `Yes — ${topFreeTool} offers a free tier suitable for startups and small teams. Browse all free ${categoryName.toLowerCase()} options in this category to compare features.`
        : `Several ${categoryName.toLowerCase()} tools offer free trials. Compare pricing options in this category or use btoolme to find budget-friendly recommendations for your stage.`
    },
    {
      question: `How do I choose the right ${categoryName.toLowerCase()} software?`,
      answer: `To choose the right ${categoryName.toLowerCase()} software, consider: your team size, technical comfort, budget (free vs paid), and the specific features you need most. btoolme's questionnaire analyzes these factors and recommends the best ${categoryName.toLowerCase()} tool for your exact business situation in under 3 minutes.`
    },
    {
      question: `What are the alternatives to ${topToolName}?`,
      answer: `Alternatives to ${topToolName} include ${categoryTools.filter(t => t.name !== topToolName).slice(0, 3).map(t => t.name).join(', ')}. Each has different strengths — compare features and pricing above to find the best fit.`
    }
  ];
  const seoTitle = `Best ${categoryName} Software 2026 - Compare Top ${categoryTools.length} Tools | btoolme`;
  const seoDescription = `Compare ${categoryTools.length} top ${categoryName.toLowerCase()} tools including ${toolNames}. Find the perfect ${categoryName.toLowerCase()} software for your business with features, pricing, and personalized recommendations.`;

  const categoryKeywords = [
    `best ${categoryName.toLowerCase()} software`,
    `${categoryName.toLowerCase()} tools`,
    `${categoryName.toLowerCase()} software comparison`,
    `top ${categoryName.toLowerCase()} tools 2026`,
    `${categoryName.toLowerCase()} for small business`,
    `${categoryName.toLowerCase()} for startups`,
    ...categoryTools.slice(0, 8).map(t => t.name),
    ...categoryTools.slice(0, 5).map(t => `${t.name} alternatives`)
  ].join(', ');

  const pricingLabel = (pricing: string[]) => {
    if (pricing.includes('free')) return 'Free tier available';
    if (pricing.includes('low')) return 'Budget-friendly';
    if (pricing.includes('medium')) return 'Mid-range';
    return 'Premium';
  };

  const lastUpdatedIso = new Date().toISOString();
  const lastUpdatedDisplay = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `Best ${categoryName} Software Tools`,
    "description": seoDescription,
    "url": `https://btoolme.com/categories/${categorySlug}`,
    "dateModified": lastUpdatedIso,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://btoolme.com" },
        { "@type": "ListItem", "position": 2, "name": "Directory", "item": "https://btoolme.com/directory" },
        { "@type": "ListItem", "position": 3, "name": categoryName, "item": `https://btoolme.com/categories/${categorySlug}` }
      ]
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": `Best ${categoryName} Software Tools`,
      "numberOfItems": categoryTools.length,
      "itemListElement": categoryTools.map((tool, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": tool.name,
          "description": tool.description,
          "url": tool.website,
          "sameAs": `https://btoolme.com/tools/${tool.id}`,
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": categoryName,
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "description": pricingLabel(tool.pricing),
            "price": tool.pricing.includes('free') ? "0" : undefined,
            "priceCurrency": "USD"
          }
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/categories/${categorySlug}`}
        schema={itemListSchema}
        keywords={categoryKeywords}
        imageUrl="https://btoolme.com/social-preview.png"
        faqItems={faqItems}
        breadcrumbs={[
          { name: 'Tool Directory', path: '/directory' },
          { name: categoryName, path: `/categories/${categorySlug}` },
        ]}
      />

      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-gray-600 mb-6" aria-label="Breadcrumb">
          <Link href="/">
            <a className="hover:text-blue-600 transition-colors">Home</a>
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link href="/directory">
            <a className="hover:text-blue-600 transition-colors">Directory</a>
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">{categoryName}</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Best {categoryName} Software 2026 - Compare Top {categoryTools.length} Tools
          </h1>
          <p className="text-sm text-gray-500 mb-4">
            <time dateTime={lastUpdatedIso}>Updated {lastUpdatedDisplay}</time>
            {' '}&middot; {categoryTools.length} tools ranked
          </p>
          <p className="text-lg text-gray-700 font-medium mb-2 max-w-3xl">
            {categoryName} software helps businesses {
              categoryName.toLowerCase().includes('accounting') ? 'track finances, send invoices, and manage bookkeeping'
              : categoryName.toLowerCase().includes('crm') || categoryName.toLowerCase().includes('sales') ? 'manage leads, close deals, and build customer relationships'
              : categoryName.toLowerCase().includes('project') ? 'plan work, track tasks, and collaborate with teams'
              : categoryName.toLowerCase().includes('marketing') ? 'reach customers, run campaigns, and grow revenue'
              : categoryName.toLowerCase().includes('communication') ? 'connect teams and share information in real time'
              : `streamline ${categoryName.toLowerCase()} workflows and improve productivity`
            }.
          </p>
          <p className="text-base text-gray-600 max-w-3xl">
            Compare the top {categoryTools.length} {categoryName.toLowerCase()} tools for small businesses, startups, and freelancers. Our editors curate, rank, and update this list regularly based on features, pricing, and real-world use.
          </p>
        </div>

        <div className="mb-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Not sure which {categoryName.toLowerCase()} tool is right for you?
          </h2>
          <p className="text-gray-600 mb-4">
            Take our quick questionnaire to get personalized recommendations based on your specific business needs.
          </p>
          <Link href="/questionnaire">
            <a className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all">
              Get Personalized Recommendations
            </a>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categoryTools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-blue-600" aria-hidden="true" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details
                key={index}
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer select-none list-none hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset">
                  <span className="font-medium text-gray-900 pr-4">{item.question}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 group-open:rotate-90 transition-transform duration-200" aria-hidden="true" />
                </summary>
                <div className="px-6 pb-5 pt-1">
                  <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Related Categories CTA */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Explore More Categories
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Browse all 36 business software categories
            </p>
            <Link href="/directory">
              <a className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2">
                View All Categories
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </Link>
          </div>
          <div className="p-6 bg-blue-50 rounded-xl border border-blue-100 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Not sure which tool to pick?
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Get a personalized recommendation in 3 minutes
            </p>
            <Link href="/questionnaire">
              <a className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Get My Recommendations
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
